import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { current } from '@reduxjs/toolkit';
import { verificationDetailEndpoints } from '../../features/detail/data';
import { estimateEndpoints } from '../../features/estimate/data';
import { applyExtractionResult } from './reducers/estimateReducers';
import type { VerificationSliceState } from './state';

/**
 * Narrows to a plain, non-array object. Rejects `null` and arrays so an `errors: [["message"]]`
 * payload is never mistaken for a field-error map (a stray numeric-index key like `"0"`).
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Reads the `errors` bag off an RTK Query rejected-mutation payload — only for a 400 `problem+json`
 * body with an object-shaped `errors` member. `undefined` for anything else: non-400 status, network
 * errors, a missing `errors` member, or an array-shaped `errors`.
 */
function readErrorsBag(payload: unknown): Record<string, unknown> | undefined {
  if (!isRecord(payload) || payload.status !== 400) return undefined;
  const data = payload.data;
  if (!isRecord(data)) return undefined;
  const errors = data.errors;
  return isRecord(errors) ? errors : undefined;
}

/** Maps a field-errors bag down to its first string message per field, dropping any field whose
 * value isn't a non-empty array of strings. */
function toFirstMessages(bag: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(bag)) {
    const first = Array.isArray(messages) ? messages[0] : undefined;
    if (typeof first === 'string') result[field] = first;
  }
  return result;
}

/**
 * Narrows an RTK Query rejected-mutation payload down to the SCA field errors, when present. Only a
 * 400 `problem+json` body with a non-empty `errors: Record<string, string[]>` member qualifies —
 * pre-existing 400s without an `errors` member (plain-string bodies, `ArgumentException` problem
 * details) and non-400 failures (network errors, 500s) return `null` (spec 055 FR — matcher must key
 * on the presence of `errors`, never on `status === 400` alone).
 */
function extractFieldErrors(payload: unknown): Record<string, string> | null {
  const bag = readErrorsBag(payload);
  if (!bag) return null;
  const messages = toFirstMessages(bag);
  return Object.keys(messages).length > 0 ? messages : null;
}

/**
 * Syncs the RTK Query verification read into the slice — the ONLY place API data enters the slice
 * (constitution §67). On a successful `getVerificationById`, hydrate `current` and snapshot the
 * server state with `structuredClone` (never a JSON round-trip), and reset edit tracking.
 *
 * This is the module slice referencing the detail feature's endpoint (the shared verification read);
 * future detail tabs hydrate the same slice from this one matcher.
 */
export function buildVerificationExtraReducers(builder: ActionReducerMapBuilder<VerificationSliceState>) {
  builder.addMatcher(
    verificationDetailEndpoints.endpoints.getVerificationById.matchFulfilled,
    (state, action) => {
      state.current = action.payload;
      state.server = structuredClone(action.payload);
      state.isEdited = false;
      state.editedFields = [];
      state.validationErrors = {};
    },
  );

  // On a successful SAVE, re-baseline the server snapshot to the just-persisted working case and
  // clear edit tracking (spec 005 FR-021 — mirrors `updateCase.matchFulfilled`).
  builder.addMatcher(
    verificationDetailEndpoints.endpoints.saveVerification.matchFulfilled,
    (state) => {
      if (state.current) {
        // `state.current` is an Immer draft (Proxy); unwrap with `current()` before cloning so
        // native `structuredClone` doesn't throw DataCloneError on the proxy.
        state.server = structuredClone(current(state.current));
      }
      state.isEdited = false;
      state.editedFields = [];
      state.validationErrors = {};
    },
  );

  // A rejected SAVE carrying field-level `errors` (spec 055 T018): the latest rejection is
  // authoritative, so replace `validationErrors` atomically (never merge) — a field omitted from a
  // later rejection must disappear, not linger from an earlier one — WITHOUT touching
  // `current` / `server` / `isEdited`.
  builder.addMatcher(
    verificationDetailEndpoints.endpoints.saveVerification.matchRejected,
    (state, action) => {
      const fieldErrors = extractFieldErrors(action.payload);
      if (!fieldErrors) return;
      state.validationErrors = fieldErrors;
    },
  );

  // SURGICAL matcher (spec 006 FR-048): on a successful estimate evaluation, store the transient
  // result AND refresh the live 008 authorization warnings — WITHOUT touching `server` / `isEdited`
  // / `editedFields` / `validationErrors`. The warnings are derived/read-only, so this never marks
  // the case edited and cannot dirty the save (the save path keeps ignoring `authorizationWarnings`).
  builder.addMatcher(
    estimateEndpoints.endpoints.evaluateEstimate.matchFulfilled,
    (state, action) => {
      state.evaluation = action.payload;
      if (state.current) {
        state.current.authorizationWarnings = action.payload.authorizationWarnings;
      }
    },
  );

  // SURGICAL matcher (spec 028 US5): on a successful manual code extraction, apply the returned procedures +
  // Diagnosis 1–4 to the working case IN PLACE — never a full case refetch — so other unsaved edits (e.g. a
  // pending Check Eligibility benefit load) are preserved. Marks the touched sections edited so SAVE persists.
  builder.addMatcher(
    estimateEndpoints.endpoints.extractProceduresFromNote.matchFulfilled,
    (state, action) => {
      applyExtractionResult(state, action.payload);
    },
  );
}
